/**
 * reportコマンドハンドラ
 *
 * JSON形式のReportElement設定を受け取り、レポートに要素を追加
 *
 * 使用例:
 * x:report {"type":"graph_builder","datasetId":"dataset_1","graphConfig":{"type":"scatter","xColumn":"x","yColumn":"y"}}
 * x:report {"type":"text_memo","content":"Analysis notes"}
 * x:report {"type":"statistics_summary","datasetId":"dataset_1","targetColumns":["col1","col2"]}
 */

import type { CommandHandler, CommandResult, ParsedCommand, CommandContext } from '../types';
import type { ReportElementUnion } from '../../../types/report.types';
import type { GraphConfig } from '../../../types/graph-builder.types';
import { createDatasetId, createColumnName, createReportElementId } from '../../../types';
import logger from '../../utils/logger';

/**
 * x:reportコマンドで受け付けるJSON形式
 */
interface ReportElementInput {
  type: string;
  // ターゲットレポート
  reportId?: string;       // 既存レポートに追加する場合のID
  newReportName?: string;  // 新規レポート作成時の名前（reportId省略時のみ使用）
  // 要素設定
  datasetId?: string;
  title?: string;
  height?: number;
  // graph_builder - GraphConfigをそのまま受け付け
  // 対応タイプ: histogram, scatter, timeseries, bar, boxplot, heatmap, pairplot, rugplot, custom
  graphConfig?: GraphConfig;
  // text_memo
  content?: string;
  // statistics_summary
  targetColumns?: string[];
  // crosstab
  crosstabConfiguration?: unknown;
}

/**
 * 入力JSONからReportElementUnionを構築
 */
function buildReportElement(
  input: ReportElementInput,
  context: CommandContext
): ReportElementUnion | null {
  const now = new Date().toISOString();
  const elementId = createReportElementId(`${input.type}-${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

  switch (input.type) {
    case 'graph_builder': {
      if (!input.datasetId || !input.graphConfig) return null;

      const dataset = context.datasets[input.datasetId];
      if (!dataset) return null;

      const datasetId = createDatasetId(input.datasetId);

      // GraphConfigをそのまま使用（型はJSON入力時に検証済み）
      // 全グラフタイプ対応: histogram, scatter, timeseries, bar, boxplot, heatmap, pairplot, rugplot, custom
      return {
        id: elementId,
        type: 'graph_builder',
        datasetId,
        graphConfig: input.graphConfig,
        title: input.title || '',
        height: input.height ?? 400,
        marginBottom: 16,
        createdAt: now,
        lastModified: now
      };
    }

    case 'statistics_summary': {
      if (!input.datasetId || !input.targetColumns || input.targetColumns.length === 0) return null;

      return {
        id: elementId,
        type: 'statistics_summary',
        datasetId: createDatasetId(input.datasetId),
        targetColumns: input.targetColumns.map(c => createColumnName(c)),
        displayFormat: 'cards',
        height: input.height ?? 300,
        marginBottom: 16,
        createdAt: now,
        lastModified: now
      };
    }

    default:
      return null;
  }
}

export const reportHandler: CommandHandler = {
  name: 'report',
  description: 'Add an element to report from JSON specification',
  category: 'visualization',
  parameters: [
    {
      name: 'json',
      type: 'string',
      required: false, // JSON-basedコマンドはpositionalArgsを使用するため、ここではfalse
      description: 'JSON object specifying the report element'
    }
  ],
  aliases: [],
  examples: [
    'x:report {"type":"graph_builder","datasetId":"dataset_1","graphConfig":{"type":"scatter","xColumn":"x","yColumn":"y"}}',
    'x:report {"type":"text_memo","content":"Analysis notes here"}',
    'x:report {"type":"statistics_summary","datasetId":"dataset_1","targetColumns":["price","quantity"]}'
  ],

  execute: async (args: ParsedCommand, context: CommandContext): Promise<CommandResult> => {
    // JSONを取得（位置引数全体を結合）
    const jsonStr = args.positionalArgs.join(' ').trim();

    if (!jsonStr) {
      return {
        success: false,
        message: 'JSON configuration required',
        error: {
          code: 'NO_JSON',
          message: 'No JSON configuration provided',
          suggestion: 'Provide a JSON object: x:report {"type":"text_memo","content":"..."}'
        }
      };
    }

    // JSONをパース
    let elementInput: ReportElementInput;
    try {
      elementInput = JSON.parse(jsonStr) as ReportElementInput;
    } catch (e) {
      logger.error('[reportHandler] Failed to parse JSON', { jsonStr, error: e });
      return {
        success: false,
        message: 'Invalid JSON format',
        error: {
          code: 'INVALID_JSON',
          message: `Failed to parse JSON: ${e instanceof Error ? e.message : String(e)}`,
          suggestion: 'Check JSON syntax'
        }
      };
    }

    // 必須フィールドの検証
    if (!elementInput.type) {
      return {
        success: false,
        message: 'Element type required',
        error: {
          code: 'NO_TYPE',
          message: 'Element type is required',
          suggestion: 'Include "type" field (graph_builder, text_memo, statistics_summary)'
        }
      };
    }

    // ReportElementを構築
    const element = buildReportElement(elementInput, context);
    if (!element) {
      return {
        success: false,
        message: `Failed to build report element`,
        error: {
          code: 'INVALID_ELEMENT',
          message: `Could not build element from type: ${elementInput.type}`,
          suggestion: 'Supported types: graph_builder, text_memo, statistics_summary. Check required fields.'
        }
      };
    }

    logger.info('[reportHandler] Adding report element', {
      type: element.type,
      datasetId: 'datasetId' in element ? element.datasetId : undefined,
      reportId: elementInput.reportId,
      newReportName: elementInput.newReportName
    });

    // レポートに追加
    const reportId = await context.addReportElement(element, {
      reportId: elementInput.reportId,
      newReportName: elementInput.newReportName
    });
    if (!reportId) {
      return {
        success: false,
        message: 'Failed to add element to report',
        error: {
          code: 'REPORT_ERROR',
          message: 'Could not create report or add element'
        }
      };
    }

    return {
      success: true,
      message: `Added ${element.type} to report`,
      data: {
        reportId,
        elementId: element.id,
        elementType: element.type
      }
    };
  }
};
